import React, { useRef } from 'react';
import { calculateEmployerPF, employeePfLabel, resolveSalaryWageCaps } from '@/lib/payrollCalc';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';

const AbdLogo = ({ height = 36 }) => (
  <svg
    aria-hidden="true"
    fill="none"
    viewBox="18 8 48 42"
    xmlns="http://www.w3.org/2000/svg"
    style={{ height, width: 'auto' }}
  >
    <path
      clipRule="evenodd"
      d="M22.533 38.094h24.408c.313 0 .04-.234-.117-.586l-1.835-3.4a15.982 15.982 0 0 1-.742-1.406H32.375v-.117l3.436-6.251c.703-1.212 1.68-3.165 2.343-4.298l2.343-4.298c.196-.351 1.016-1.992 1.211-2.149.04.196.117.313.235.47l.742 1.328c.859 1.445 1.718 3.047 2.577 4.454 1.015 1.719 2.07 3.672 3.085 5.352l2.07 3.595c.156.273.312.586.508.898.351.586.664 1.172 1.054 1.798.352.586.664 1.172 1.015 1.797l4.14 7.189c.117.234.976 1.602 1.015 1.797H18.667v5.353h46.121c.118 0 .157-.04.157-.156v-4.845c0-.196-.47-.938-.586-1.133l-3.163-5.51c-.118-.195-.196-.35-.313-.546L54.83 26.92c-.117-.196-.195-.352-.313-.547l-6.053-10.471c-.117-.196-.195-.352-.312-.547L45.3 10.393c-.156-.235-.586-.938-.625-1.172h-5.819c-.039.195-.351.664-.468.898-.196.313-.313.586-.508.899l-1.015 1.797c-.196.313-.313.586-.508.899a6.875 6.875 0 0 1-.547.899l-2.07 3.633c-.351.625-.663 1.211-1.015 1.797l-6.248 10.862c-.352.625-.664 1.211-1.016 1.797l-2.616 4.532c-.274.508-.586.82-.274.82"
      fill="#B5171B"
      fillRule="evenodd"
    />
  </svg>
);

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const ann = (v) => Number(v || 0) * 12;

const buildCtc = (employee, projects = []) => {
  const sal = employee?.salary || {};
  const basic = Number(sal.basic) || 0;
  const hra = Number(sal.hra) || 0;
  const specialAllowance = Number(sal.specialAllowance) || 0;
  const conveyance = Number(sal.conveyance) || 0;
  const telephoneExpense = Number(sal.telephoneExpense) || 0;
  const mediclaim = Number(sal.mediclaim) || 0;
  const tds = Number(sal.tds) || 0;
  const epf = Number(sal.epf) || 0;
  const esic = Number(sal.esic) || 0;
  const bonus = Number(sal.bonus) || 0;
  const wageCaps = resolveSalaryWageCaps(sal, {
    assignedProjectIds: employee?.projects,
    projects,
  });
  const employerPF =
    sal.employerPF != null ? Number(sal.employerPF) || 0 : calculateEmployerPF(basic, wageCaps.pfLimit);
  const employerESIC = Number(sal.employerESIC) || 0;
  const gratuity = Number(sal.gratuity) || 0;
  const salGross = basic + hra + specialAllowance;
  const totalReimb = conveyance;
  const totalDed = epf + esic + tds;
  const inHand = salGross + totalReimb - totalDed;
  const employerTotal = employerPF + employerESIC + gratuity + mediclaim + telephoneExpense;
  const ctc = salGross + totalReimb + bonus + employerTotal;
  const extra = sal.extraReimbursement || {};
  const extraFreq = extra.extraReimbursementFrequency === 'Yearly' ? 'Yearly' : 'Monthly';
  const extraAmt = Number(extra.extraReimbursementAmount) || 0;
  const monthlyExtra = extraFreq === 'Yearly' ? Math.round(extraAmt / 12) : extraAmt;
  const annualExtra = extraFreq === 'Yearly' ? extraAmt : extraAmt * 12;
  const inHandAfterExtra =
    Number(extra.inHandSalaryAfterExtraReimbursement) || inHand + monthlyExtra;
  const inHandAfterExtraAnnual =
    Number(extra.inHandSalaryAfterExtraReimbursementPerAnnum) || inHand * 12 + annualExtra;
  const ctcAfterExtra =
    Number(extra.totalCtcAfterExtraReimbursement) || ctc + monthlyExtra;
  const ctcAfterExtraAnnual =
    Number(extra.totalCtcAfterExtraReimbursementPerAnnum) || ctc * 12 + annualExtra;

  return {
    basic,
    hra,
    specialAllowance,
    conveyance,
    telephoneExpense,
    mediclaim,
    tds,
    epf,
    esic,
    bonus,
    employerPF,
    employerESIC,
    gratuity,
    salGross,
    totalReimb,
    totalDed,
    inHand,
    employerTotal,
    ctc,
    extraFreq,
    monthlyExtra,
    annualExtra,
    inHandAfterExtra,
    inHandAfterExtraAnnual,
    ctcAfterExtra,
    ctcAfterExtraAnnual,
    projectLabel: wageCaps.projectLabel,
  };
};

const Row = ({ label, monthly, annual, bold, highlight, alwaysShow = false }) => {
  if (!alwaysShow && Number(monthly || 0) === 0 && Number(annual || 0) === 0) return null;
  return (
    <tr style={{ background: highlight ? '#f8fafc' : undefined, fontWeight: bold ? 600 : 400 }}>
      <td style={{ padding: '7px 10px', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{label}</td>
      <td style={{ padding: '7px 10px', fontSize: '13px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: bold ? '#0047AB' : undefined }}>
        {fmt(monthly)}
      </td>
      <td style={{ padding: '7px 10px', fontSize: '13px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: bold ? '#0047AB' : undefined }}>
        {fmt(annual)}
      </td>
    </tr>
  );
};

const SectionHeader = ({ label, code, show = true }) => {
  if (!show) return null;
  return (
    <tr style={{ background: 'rgba(0, 71, 171, 0.05)' }}>
      <td colSpan={3} style={{ padding: '8px 10px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#0047AB', borderBottom: '1px solid rgba(0, 71, 171, 0.2)' }}>
        {label}{code ? ` (${code})` : ''}
      </td>
    </tr>
  );
};

export function CtcSlipModal({ open, onOpenChange, employee, projects = [] }) {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const name = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || 'Employee';
    printWindow.document.write(`
      <html>
        <head>
          <title>CTC Structure - ${name}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; padding: 32px; color: #0f172a; background: #fff; }
            h1, h2, h3 { font-family: 'Manrope', sans-serif; }
            table { width: 100%; border-collapse: collapse; }
            @media print {
              body { padding: 16px; }
              .no-print { display: none; }
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  if (!employee) return null;

  const c = buildCtc(employee, projects);
  const empId = employee?.empId || employee?.id || '-';
  const empName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || '-';
  const hasEarnings = c.basic > 0 || c.hra > 0 || c.specialAllowance > 0;
  const hasReimb = c.conveyance > 0 || c.monthlyExtra > 0;
  const hasDeductions = c.epf > 0 || c.esic > 0 || c.tds > 0;
  const hasBonus = c.bonus > 0;
  const hasEmployer =
    c.employerPF > 0 || c.employerESIC > 0 || c.telephoneExpense > 0 || c.mediclaim > 0 || c.gratuity > 0;
  const hasExtra = c.monthlyExtra > 0 || c.annualExtra > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogTitle className="sr-only">
          CTC Structure - {empName}
        </DialogTitle>

        <div ref={printRef} className="w-full min-w-0">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0047AB', paddingBottom: '16px', marginBottom: '20px', width: '100%' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AbdLogo />
                <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '24px', fontWeight: 700, color: '#B5171B', margin: 0 }}>
                  ABD
                </h1>
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Salary CTC Structure</p>
              {c.projectLabel && (
                <p style={{ fontSize: '11px', color: '#0047AB', marginTop: '4px', fontWeight: 600 }}>
                  PF/ESIC calculated using project: {c.projectLabel}
                </p>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Employee ID</p>
              <p style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{empId}</p>
            </div>
          </div>

          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, marginBottom: '8px' }}>Employee Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 32px', marginBottom: '20px', fontSize: '13px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Name</span>
              <span style={{ fontWeight: 500 }}>{empName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Department</span>
              <span style={{ fontWeight: 500 }}>{employee?.department || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Designation</span>
              <span style={{ fontWeight: 500 }}>{employee?.designation || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Joining Date</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee?.joiningDate || '-'}</span>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#64748b', borderBottom: '2px solid #e2e8f0', width: '50%' }}>Salary Component</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#64748b', borderBottom: '2px solid #e2e8f0', width: '25%' }}>Per Month</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#64748b', borderBottom: '2px solid #e2e8f0', width: '25%' }}>Per Annum</th>
              </tr>
            </thead>
            <tbody>
              <SectionHeader label="Earnings" show={hasEarnings} />
              <Row label="Basic" monthly={c.basic} annual={ann(c.basic)} />
              <Row label="House Rent Allowance" monthly={c.hra} annual={ann(c.hra)} />
              <Row label="Special Allowance" monthly={c.specialAllowance} annual={ann(c.specialAllowance)} />
              <Row label="Gross Salary (A)" monthly={c.salGross} annual={ann(c.salGross)} highlight bold />

              <SectionHeader label="Reimbursements" code="B" show={hasReimb} />
              <Row label="Conveyance Reimbursements (Driver, Petrol)" monthly={c.conveyance} annual={ann(c.conveyance)} />
              <Row label="Total Reimbursements (B)" monthly={c.totalReimb} annual={ann(c.totalReimb)} highlight bold />
              <Row label={`Extra Reimbursement (${c.extraFreq})`} monthly={c.monthlyExtra} annual={c.annualExtra} />

              <SectionHeader label="Employee Deductions" code="C" show={hasDeductions} />
              <Row label={employeePfLabel(employee?.salary?.applyPfCeilingToEmployee)} monthly={c.epf} annual={ann(c.epf)} />
              <Row label="ESIC (0.75% of Gross)" monthly={c.esic} annual={ann(c.esic)} />
              <Row label="TDS" monthly={c.tds} annual={ann(c.tds)} />
              <Row label="Total Deductions (C)" monthly={c.totalDed} annual={ann(c.totalDed)} highlight bold />

              {c.inHand !== 0 && (
                <tr style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, borderBottom: '1px solid rgba(16, 185, 129, 0.3)' }}>In Hand Salary (A + B - C)</td>
                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#10b981', borderBottom: '1px solid rgba(16, 185, 129, 0.3)' }}>{fmt(c.inHand)}</td>
                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#10b981', borderBottom: '1px solid rgba(16, 185, 129, 0.3)' }}>{fmt(ann(c.inHand))}</td>
                </tr>
              )}
              {hasExtra && (
                <tr style={{ background: 'rgba(16, 185, 129, 0.05)' }}>
                  <td style={{ padding: '8px 10px', fontSize: '13px', borderBottom: '2px solid rgba(16, 185, 129, 0.3)' }}>In Hand + Extra Reimbursement</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 600, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#10b981', borderBottom: '2px solid rgba(16, 185, 129, 0.3)' }}>{fmt(c.inHandAfterExtra)}</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 600, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#10b981', borderBottom: '2px solid rgba(16, 185, 129, 0.3)' }}>{fmt(c.inHandAfterExtraAnnual)}</td>
                </tr>
              )}

              <SectionHeader label="Bonus" code="D" show={hasBonus} />
              <Row label="Bonus (8.33% of Basic) (Paid Annually)" monthly={c.bonus} annual={ann(c.bonus)} />

              <SectionHeader label="Employer Contributions" code="E" show={hasEmployer} />
              <Row label="Employer PF Contribution (13% of Basic)" monthly={c.employerPF} annual={ann(c.employerPF)} />
              <Row label="ESIC (3.25% of Gross)" monthly={c.employerESIC} annual={ann(c.employerESIC)} />
              <Row label="Telephone Expense" monthly={c.telephoneExpense} annual={ann(c.telephoneExpense)} />
              <Row label="Mediclaim Insurance Premium" monthly={c.mediclaim} annual={ann(c.mediclaim)} />
              <Row label="Gratuity (4.81% of Basic)" monthly={c.gratuity} annual={ann(c.gratuity)} />
              <Row label="Employer Contributions (E)" monthly={c.employerTotal} annual={ann(c.employerTotal)} highlight bold />

              {c.ctc !== 0 && (
                <tr style={{ background: 'rgba(0, 71, 171, 0.1)' }}>
                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, borderBottom: '1px solid rgba(0, 71, 171, 0.3)' }}>CTC (A + B + D + E)</td>
                  <td style={{ padding: '10px', fontSize: '15px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#0047AB', borderBottom: '1px solid rgba(0, 71, 171, 0.3)' }}>{fmt(c.ctc)}</td>
                  <td style={{ padding: '10px', fontSize: '15px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#0047AB', borderBottom: '1px solid rgba(0, 71, 171, 0.3)' }}>{fmt(ann(c.ctc))}</td>
                </tr>
              )}
              {hasExtra && (
                <tr style={{ background: 'rgba(0, 71, 171, 0.05)' }}>
                  <td style={{ padding: '8px 10px', fontSize: '13px' }}>CTC + Extra Reimbursement</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#0047AB' }}>{fmt(c.ctcAfterExtra)}</td>
                  <td style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#0047AB' }}>{fmt(c.ctcAfterExtraAnnual)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
            This is a computer-generated CTC structure and does not require a signature.
          </div>
        </div>

        <div className="no-print flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="print-ctc-btn">
            <Printer className="w-4 h-4 mr-2" /> Print CTC
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
